// src/modules/competitions/components/CompactUploader.tsx

import React, { useState, useEffect } from 'react';
import Button from '../../../shared/components/ui/Button';
import { CompetitionImageType } from '../types';
import { imageTypesService } from '../services/imageTypesService';
import '../styles/compactUploader.css';

interface Props {
    imageType: CompetitionImageType;
    onFileSelect: (file: File, previewUrl: string) => void;
    onUpload: () => void;
    onCancel: () => void;
    isUploading: boolean;
    selectedFile: File | null;
    previewUrl: string | null;
    error: string | null;
}

const CompactUploader: React.FC<Props> = ({
    imageType,
    onFileSelect,
    onUpload,
    onCancel,
    isUploading,
    selectedFile,
    previewUrl,
    error
}) => {
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar archivo
        const validation = imageTypesService.validateImageFile(file, imageType);
        if (!validation.isValid) {
            alert(validation.error);
            e.target.value = '';
            return;
        }

        // Generar preview
        const url = URL.createObjectURL(file);
        onFileSelect(file, url);
    };

    return (
        <div className="compact-uploader">
            {!selectedFile ? (
                <div className="uploader-initial">
                    <input
                        type="file"
                        accept={imageType.allowed_formats.map(f => `image/${f}`).join(',')}
                        onChange={handleFileChange}
                        className="file-input-hidden"
                        id="compact-file-input"
                    />
                    <label htmlFor="compact-file-input" className="upload-button-label">
                        <span className="upload-icon">📤</span>
                        <span className="upload-text">Subir imagen</span>
                    </label>
                    <p className="upload-hint">
                        {imageType.allowed_formats.map(f => f.toUpperCase()).join(', ')} · 
                        Máx {imageType.max_file_size_mb}MB
                    </p>
                </div>
            ) : (
                <div className="uploader-preview">
                    {previewUrl && (
                        <div className="preview-image-container">
                            <img src={previewUrl} alt="Preview" className="preview-image" />
                        </div>
                    )}
                    <div className="preview-info">
                        <p className="preview-filename">✓ {selectedFile.name}</p>
                        <p className="preview-filesize">
                            {imageTypesService.formatFileSize(selectedFile.size)}
                        </p>
                    </div>
                    <div className="preview-actions">
                        <Button
                            onClick={onUpload}
                            disabled={isUploading}
                            variant="primary"
                            className="btn-confirm"
                        >
                            {isUploading ? 'Subiendo...' : '✓ Confirmar subida'}
                        </Button>
                        <Button
                            onClick={onCancel}
                            disabled={isUploading}
                            variant="secondary"
                            className="btn-cancel"
                        >
                            Cancelar
                        </Button>
                    </div>
                </div>
            )}
            {error && (
                <div className="uploader-error">
                    <span className="error-icon">⚠️</span>
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};

export default CompactUploader;